import React, { BaseSyntheticEvent, useState, useRef, useEffect } from 'react'
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonListHeader,
  IonLabel,
  IonList,
  IonItem,
  IonFooter,
  IonButtons,
  IonButton,
  IonToggle,
  IonSearchbar,
  IonPopover,
  IonItemDivider,
  IonIcon,
} from '@ionic/react'
import { useTranslation } from 'react-i18next'
import { IOption, IOptions, IPNNVOption } from '../../../common/common.types'
import { useDispatch, useSelector } from 'react-redux'
import { getStyleForPHI, IPHIComponent } from '../../../components/phi/phi.component'
import { isIOSDevice } from '../../../utilities/isiOS.util'
import useDeviceMetrics from '../../../utilities/useDeviceMetrics'
import { preventPageScroll } from '../../../utilities/preventPageScroll.util'
import { deepCopy } from '../../../utilities/array.utils'
import { IAlertState } from '../../../components/alert/store/alert.types'
import { showAlert } from '../../../components/alert/store/alert.actions'
import { ResizableBox } from 'react-resizable'
import { getCalculatedHeight } from '../../../utilities/popover.util'
import { ICrewMember } from '../store/crew.types'
import { getCrewSelector } from '../selectors'
import { setRole, setRolePNNV, setPreceptorStudent } from '../store/crew.actions'
import { getDatapointsConfigSelector } from '../../configurations/selectors'
import { getServiceConfigSelector } from '../../configurations/selectors/serviceConfig.selector'
import { useWindowSize } from '../../../hooks/useWindowSize'
import './CrewRoleSelect.component.css'
import { caretDownOutline } from 'ionicons/icons'

interface ICrewRoleSelectProps extends IPHIComponent {
  crewMember: ICrewMember
}

const CrewRoleSelect: React.FC<ICrewRoleSelectProps> = (props) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { crewMember, isPHI } = props
  const crew = useSelector(getCrewSelector)
  const { roleOptions, crewList, preceptorStudentOptions } = crew
  const datapointsConfig = useSelector(getDatapointsConfigSelector)
  const serviceConfig = useSelector(getServiceConfigSelector)
  const { DP_CREW_PS } = datapointsConfig
  const { PR_CREW_PS } = serviceConfig
  const showPreceptorStudentSection = DP_CREW_PS !== 0 && PR_CREW_PS !== 0
  const { isTablet: isTabletDevice } = useDeviceMetrics()
  const isIOS = isIOSDevice()

  const [isOpen, setIsOpen] = useState(false)
  const [searchText, setSearchText] = useState('')
  const popoverEventRef = useRef<Event | undefined>(undefined)
  const itemRef = useRef<HTMLIonItemElement>(null)
  const preceptorToggleRef = useRef<HTMLIonToggleElement>(null)
  const [heightMeasures, setHeightMeasures] = useState(getCalculatedHeight(itemRef.current, 550))
  const [localRoleId, setLocalRoleId] = useState<IOptions>([])
  const [localPreceptorStudent, setLocalPreceptorStudent] = useState<IOption | null>(null)
  const [localRoleIdPNNV, setLocalRoleIdPNNV] = useState<IPNNVOption | null>(null)

  const pnnvOptions = roleOptions.filter((option: IOption) => option.isNV || option.isPN)
  const multiSelectOptions = roleOptions.filter((option: IOption) => !option.isNV && !option.isPN)

  const search = searchText.toLowerCase().trim()
  const filteredOptions = search
    ? multiSelectOptions.filter((o: IOption) => o.name?.toLowerCase().includes(search))
    : multiSelectOptions

  const { width, height } = useWindowSize()

  const updateHeight = () => {
    setHeightMeasures(getCalculatedHeight(itemRef.current, 550))
  }

  useEffect(() => {
    updateHeight()
  }, [width, height])

  useEffect(() => {
    updateHeight()
  }, [isOpen])

  const showModalHandler = (e: React.MouseEvent<HTMLIonItemElement>) => {
    preventPageScroll(true)
    setLocalRoleId(deepCopy(crewMember.roleId))
    setLocalPreceptorStudent(crewMember.preceptorStudent || null)
    setLocalRoleIdPNNV(crewMember.roleIdPNNV || null)
    setSearchText('')
    if (isTabletDevice) {
      e.persist()
      popoverEventRef.current = e.nativeEvent
    }
    setIsOpen(true)
  }

  const handleSearch = (e: CustomEvent) => {
    setSearchText(e.detail.value)
  }

  const dismiss = () => {
    setSearchText('')
    setIsOpen(false)
    popoverEventRef.current = undefined
    preventPageScroll(false)
  }

  const save = () => {
    dispatch(setRole(localRoleId, crewMember))
    dispatch(setPreceptorStudent(localPreceptorStudent, crewMember))
    dispatch(setRolePNNV(localRoleIdPNNV, crewMember))
    dismiss()
  }

  const handleMultiSelectToggle = (e: BaseSyntheticEvent, option: IOption) => {
    const currentValues = deepCopy(localRoleId)
    if (e.target.checked) {
      currentValues.push(option)
      if (localRoleIdPNNV) {
        setLocalRoleIdPNNV(null)
      }
    } else {
      const index = currentValues.findIndex((o: IOption) => o.id === option.id)
      if (index !== -1) {
        currentValues.splice(index, 1)
      }
    }
    setLocalRoleId(currentValues)
  }

  const handlePreceptorStudentToggle = (e: BaseSyntheticEvent, option: IOption) => {
    const isChecked = e.target.checked

    if (isChecked && String(option.id) === 'P') {
      const hasOtherPreceptor = crewList.some(
        (member: ICrewMember) =>
          member.preceptorStudent && String(member.preceptorStudent.id) === 'P' && member.key !== crewMember.key
      )

      if (hasOtherPreceptor) {
        // as  the toggle was turning on even if we return from here, we need to reset the toggle to false
        if (preceptorToggleRef.current) {
          preceptorToggleRef.current.checked = false
        }
        const alertState: IAlertState = {
          header: t('shift.crewDetails.form.PreceptorStudent'),
          message: t('shift.crewDetails.form.OnlyOnePreceptorAllowed'),
          buttons: [
            {
              text: t('common.buttons.Ok'),
            },
          ],
        }
        dispatch(showAlert(alertState))
        return
      }
    }

    if (isChecked && localRoleIdPNNV) {
      setLocalRoleIdPNNV(null)
    }

    setLocalPreceptorStudent(isChecked ? (option as IOption) : null)
  }

  const showConfirmationBeforeClearingRegularValues = (e: BaseSyntheticEvent, option: IOption) => {
    const alertState: IAlertState = {
      header: t('shift.crewDetails.form.Role'),
      message: t('pnnv.userConfirmationMessage'),
      buttons: [
        {
          text: t('pnnv.cancel'),
          handler: () => {
            setLocalRoleIdPNNV(null)
          },
        },
        { text: t('pnnv.continueAndClear'), role: 'destructive', handler: () => selectPNNVOption(e, option) },
      ],
      showEqualWidthButton: true,
    }
    dispatch(showAlert(alertState))
  }

  const checkForConfirmation = (e: BaseSyntheticEvent, option: IOption) => {
    e.preventDefault()
    const hasRegularValues = localRoleId?.length > 0
    const hasPreceptorStudent = showPreceptorStudentSection && localPreceptorStudent
    if (e.target.checked && option.name && (hasRegularValues || hasPreceptorStudent)) {
      setPNNVValue(option)
      showConfirmationBeforeClearingRegularValues(e, option)
    } else {
      selectPNNVOption(e, option)
    }
  }

  const setPNNVValue = (option: IOption) => {
    const obj = {
      id: option.id,
      name: option.name,
      pn: '',
      nv: '',
    }
    const key = 'nemsisCode' as keyof IOption
    if (option[key]) {
      if (option.isPN) {
        obj.pn = String(option[key])
      } else if (option.isNV) {
        obj.nv = String(option[key])
      }
    }
    setLocalRoleIdPNNV(obj as IPNNVOption)
  }

  const selectPNNVOption = (e: BaseSyntheticEvent, option: IOption) => {
    e.preventDefault()
    if (e.target.checked && option.name) {
      setPNNVValue(option)
      if (localRoleId?.length > 0) {
        setLocalRoleId([])
      }
      if (localPreceptorStudent) {
        setLocalPreceptorStudent(null)
      }
    } else {
      setLocalRoleIdPNNV(null)
    }
  }

  const selectedOptionNames =
    crewMember.roleIdPNNV && crewMember.roleIdPNNV.name
      ? crewMember.roleIdPNNV.name
      : crewMember.roleId.map((o: IOption) => o.name || '').join(', ')

  const renderContent = () => (
    <>
      <IonHeader className='ion-no-border'>
        <IonToolbar color='light'>
          <IonListHeader color='light'>
            <IonLabel>{t('shift.crewDetails.form.Role')}</IonLabel>
          </IonListHeader>
        </IonToolbar>
        <IonToolbar color='light'>
          <IonSearchbar
            data-testid='Searchbar'
            debounce={0}
            value={searchText}
            onIonChange={handleSearch}
            autocomplete='off'
          />
        </IonToolbar>
      </IonHeader>
      <IonList id='multiSelectList'>
        {filteredOptions &&
          filteredOptions.map((option: IOption) => (
            <IonItem
              data-private={isPHI}
              style={getStyleForPHI(isPHI)}
              key={`Option_${option.id}`}
              data-skip-auto-enhance>
              <IonLabel>{option.name}</IonLabel>
              <IonToggle
                slot='start'
                name={option.name}
                value={`${option.id}`}
                checked={localRoleId.some((o: IOption) => o.id === option.id)}
                onClick={(e) => handleMultiSelectToggle(e, option)}
              />
            </IonItem>
          ))}
        {searchText.length > 0 && !filteredOptions.length && (
          <IonItem key={'no_options_found'}>
            <IonLabel>
              {t('common.NoSearchResults')}
              {`"${searchText}"`}
            </IonLabel>
          </IonItem>
        )}
      </IonList>
      {showPreceptorStudentSection && (
        <div className='preceptor-student-section'>
          <IonItemDivider color='light' className='ion-no-padding-top ion-no-padding-bottom pt-5 pb-5'>
            <IonLabel className='grid-item-divider-label' color='medium'>
              {t('shift.crewDetails.form.PreceptorStudentQuestion')}
            </IonLabel>
          </IonItemDivider>
          <IonList lines='inset' className='ion-no-padding pt-0'>
            {preceptorStudentOptions.map((option: IOption) => (
              <IonItem key={option.id} data-testid={`PreceptorStudent_${option.id}`} data-skip-auto-enhance>
                <IonLabel>{option.name}</IonLabel>
                <IonToggle
                  ref={String(option.id) === 'P' ? preceptorToggleRef : null}
                  slot='start'
                  name={option.name}
                  value={`${option.id}`}
                  checked={String(localPreceptorStudent?.id) === String(option.id)}
                  onClick={(e) => handlePreceptorStudentToggle(e, option)}
                />
              </IonItem>
            ))}
          </IonList>
        </div>
      )}
      {pnnvOptions && pnnvOptions.length > 0 && (
        <div className='pnnv-selection-section'>
          <hr className='picklist-divider' />
          <IonList lines='inset'>
            {pnnvOptions.map((option: IOption) => (
              <IonItem
                key={option.id}
                data-testid={`Option_${option.id}`}
                className={`${localRoleIdPNNV?.id === option.id ? 'selected-pnnv-option' : ''} pnnv-option`}
                data-skip-auto-enhance>
                <IonLabel id={`optionLabel_${option.id}`}>{option.name}</IonLabel>
                <IonToggle
                  slot='start'
                  name={option.name}
                  value={`${option.id}`}
                  disabled={!!localRoleIdPNNV && localRoleIdPNNV?.id !== option.id}
                  checked={localRoleIdPNNV?.id === option.id}
                  onClick={(e) => checkForConfirmation(e, option)}
                />
              </IonItem>
            ))}
          </IonList>
        </div>
      )}
      <IonFooter className='ion-no-border'>
        <IonToolbar color='light' className='p-10'>
          <IonButtons>
            <IonButton
              data-testid='CrewRoleSelectCancel'
              className='btn btn-secondary mr-8'
              expand='block'
              onClick={dismiss}>
              {t('common.buttons.Cancel')}
            </IonButton>
            <IonButton data-testid='CrewRoleSelectSave' className='btn btn-primary' expand='block' onClick={save}>
              {t('common.buttons.Save')}
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonFooter>
    </>
  )

  return (
    <div>
      {!isTabletDevice && (
        <IonModal
          data-testid='CrewRoleSelectModal'
          showBackdrop={isOpen}
          isOpen={isOpen}
          onDidDismiss={dismiss}
          className={isIOS ? 'multi-select-modal multi-select-modal-ios' : 'multi-select-modal'}>
          {renderContent()}
        </IonModal>
      )}

      {isTabletDevice && (
        <IonPopover
          data-testid='CrewRoleSelectPopover'
          showBackdrop={isOpen}
          isOpen={isOpen}
          event={popoverEventRef.current}
          onDidDismiss={dismiss}
          className='multi-select-popover'>
          <ResizableBox
            width={400}
            height={heightMeasures.height}
            minConstraints={[300, 350]}
            maxConstraints={[550, heightMeasures.maxHeight]}>
            {renderContent()}
          </ResizableBox>
        </IonPopover>
      )}

      <IonItem
        ref={itemRef}
        button
        detail={false}
        className={`multi-select-ion-item ${
          selectedOptionNames?.length > 0 && showPreceptorStudentSection && crewMember.preceptorStudent?.name
            ? 'selected-crew-option-item'
            : ''
        }`}
        data-testid={`CrewRoleSelect_${crewMember.userId}`}
        lines='none'
        onClick={showModalHandler}>
        <IonLabel
          position={
            selectedOptionNames || (showPreceptorStudentSection && crewMember.preceptorStudent?.name)
              ? 'floating'
              : undefined
          }
          className={
            selectedOptionNames || (showPreceptorStudentSection && crewMember.preceptorStudent?.name)
              ? 'multi-select-ion-label-floating'
              : 'multi-select-ion-label'
          }
          color='medium'>
          {t('shift.crewDetails.form.Role')}
        </IonLabel>
        {selectedOptionNames && (
          <div data-private={isPHI} style={getStyleForPHI(isPHI)} className='selected-option ion-no-padding'>
            {selectedOptionNames}
          </div>
        )}
        {showPreceptorStudentSection && crewMember.preceptorStudent?.name && (
          <div data-private={isPHI} style={getStyleForPHI(isPHI)} className='preceptor-student-option'>
            {crewMember.preceptorStudent.name}
          </div>
        )}
        <IonIcon slot='end' size='small' color='medium' className='ion-icon-end' icon={caretDownOutline} />
      </IonItem>
    </div>
  )
}

export default CrewRoleSelect
